import { useAuthStore, usePermissionStore } from '@/store';
import { PageLoader } from '@/shared/components/ui/PageLoader';
import { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { useOnlineStatus } from '@/shared/hooks';
import { App as AntApp } from 'antd';
import { useNotification } from '@/shared/utils/notification';
import { useDialogService } from '@/shared/utils/dialog';
import AppRouter from './AppRouter';
import { AuthContext } from './AuthContext';

/**
 * Internal App component that uses the notification hook
 * This must be wrapped by AntApp to access the notification API
 *
 * Menu and permissions are loaded during login and stored in permissionStore.
 * No separate menu fetch needed - sidebar uses data from permissionStore.
 */
function AppContent() {
  const { isAuthenticated, isInitialized, initializeFromStorage } =
    useAuthStore();
  const initializePermissionsFromStorage = usePermissionStore(
    state => state.initializeFromStorage,
  );

  // Initialize notification service (sets up the singleton)
  useNotification();

  // Initialize dialog service (sets up the singleton to fix Modal context warning)
  useDialogService();

  // Monitor network status and show notifications
  useOnlineStatus();

  // Session validation is handled by backend middleware (validateSessionMiddleware)
  // No frontend polling needed - backend returns 401 when user logs in elsewhere

  // Initialize auth and permissions from storage on app mount
  useEffect(() => {
    initializeFromStorage();
    initializePermissionsFromStorage();
  }, [initializeFromStorage, initializePermissionsFromStorage]);

  // Show loading while initializing auth state
  if (!isInitialized) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated }}>
      <Router>
        <AppRouter isAuthenticated={isAuthenticated} />
      </Router>
    </AuthContext.Provider>
  );
}

/**
 * Root application component
 * Sets up global providers and error boundaries
 */
function App() {
  return (
    <AntApp>
      <AppContent />
    </AntApp>
  );
}

export default App;
